import React, { useEffect, useState, useRef } from "react";
import bgImg from "../../assets/images/bg-img-1.png";
import { getAirlines, getUserProfile, updateUserProfile, updateProfilePicture, getProfilePicture } from "apis/settingsServices";
import { useAuth } from "../../contexts/AuthContext";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { useToastStore } from "store/toastStore";
import AsyncSelect from "react-select/async";
import DeleteAccountModal from "./components/DeleteAccountModal";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { GroupBase, StylesConfig } from "react-select";

// Define proper interfaces for the airline option type
interface AirlineOption {
  value: string | number;
  label: string;
}

interface AirlineData {
  id: number;
  airlineName: string;
}

interface HomebaseData {
  id: number;
  name: string;
}

interface UserProfileData {
  email: string;
  fullName: string;
  homebase: HomebaseData;
  primaryAirline: AirlineData;
  additionalAirlines: AirlineData[];
  // Add other properties as needed
}

const customAsyncStyles: StylesConfig<AirlineOption, false, GroupBase<AirlineOption>> = {
  // control: (provided, state) => ({
  //   ...provided,
  //   backgroundColor: "#F5F9FE",
  //   border: "none",
  //   boxShadow: state.isFocused ? "0 0 0 1px #C2E0FF" : "none",
  //   minHeight: "50px",
  //   borderRadius: "8px",
  //   fontSize: "14px",
  //   paddingLeft: "4px",
  //   transition: "all 0.2s ease-in-out",
  // }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#F5F9FE",
    border: "none",
    minHeight: "50px",
    borderRadius: "8px",
    fontSize: "14px",
    paddingLeft: "4px",
    transition: "all 0.2s ease-in-out",
    boxShadow: "none !important",
    outline: "none !important",

    "&:hover": {
      boxShadow: "none",
    },

    // removes react-select default focus ring
    "&.react-select__control--is-focused": {
      boxShadow: "none !important",
      outline: "none !important",
      border: "none",
    }
  }),

  input: (provided) => ({
    ...provided,
    color: "#000",
    boxShadow: "none !important",
    outline: "none !important",
    border: "none !important",

    "& input": {
      boxShadow: "none !important",
      outline: "none !important",
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#F5F9FE",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    zIndex: 50,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#DCEEFF" : state.isFocused ? "#EAF4FF" : "#F5F9FE",
    color: "#000",
    cursor: "pointer",
    padding: "10px 12px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#000",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
  }),
  // input: (provided) => ({
  //   ...provided,
  //   color: "#000",
  // }),
  indicatorsContainer: (provided) => ({
    ...provided,
    color: "#6B7280",
  }),
};

const Profile: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const { username, email } = useUserStore((state) => state);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [error, setError] = useState<string>("");
  const { setIsAuthenticated } = useStore();
  const { showToast } = useToastStore();
  const [showAdditionalAirline, setShowAdditionalAirline] = useState(false);
  const [airline1, setAirline1] = useState<AirlineOption | null>(null);
  const [airline2, setAirline2] = useState<AirlineOption | null>(null);
  const [airline3, setAirline3] = useState<AirlineOption | null>(null);
  const [airline4, setAirline4] = useState<AirlineOption | null>(null);
  const [originalAirline1, setOriginalAirline1] = useState<AirlineOption | null>(null);
  const [originalAirline2, setOriginalAirline2] = useState<AirlineOption | null>(null);
  const [originalAirline3, setOriginalAirline3] = useState<AirlineOption | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string>("images/avatar.png");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      showLoader();
      try {
        const profileData = await getUserProfile(); // Fetch the user profile
        setUserProfile(profileData); // Set the fetched data in the state

        // Fetch the profile picture
        const profilePictureData = await getProfilePicture();
        if (profilePictureData) {
          setProfilePhoto(profilePictureData.profilePictureUrl);
        }

        // Initialize the additional airline selections if they exist
        if (profileData.additionalAirlines && profileData.additionalAirlines.length > 0) {
          setShowAdditionalAirline(true);

          if (profileData.additionalAirlines[0]) {
            const airline1Option = {
              value: profileData.additionalAirlines[0].id,
              label: profileData.additionalAirlines[0].airlineName,
            };
            setAirline1(airline1Option);
            setOriginalAirline1(airline1Option);
          }

          if (profileData.additionalAirlines[1]) {
            const airline2Option = {
              value: profileData.additionalAirlines[1].id,
              label: profileData.additionalAirlines[1].airlineName,
            };
            setAirline2(airline2Option);
            setOriginalAirline2(airline2Option);
          }
          if (profileData.additionalAirlines[2]) {
            const option3: AirlineOption = {
              value: profileData.additionalAirlines[2].id,
              label: profileData.additionalAirlines[2].airlineName,
            };
            setAirline3(option3);
            setOriginalAirline3(option3);
          }
        }

        console.log(profileData);
      } catch (err) {
        //setError('Error fetching user profile'); // Set error state if fetching fails
        //showToast('Error fetching user profile', 'error', 3000);
      } finally {
        hideLoader();
      }
    };

    fetchUserProfile(); // Call the function to fetch data
  }, [showToast]);

  // Check for changes whenever the airline selections change
  useEffect(() => {
    // Function to compare two airline options (handles null cases too)
    const areOptionsEqual = (option1: AirlineOption | null, option2: AirlineOption | null) => {
      if (!option1 && !option2) return true;
      if (!option1 || !option2) return false;
      return option1.value === option2.value;
    };

    const hasAirlineChanges =
      !areOptionsEqual(airline1, originalAirline1) ||
      !areOptionsEqual(airline2, originalAirline2) ||
      !areOptionsEqual(airline3, originalAirline3);

    setHasChanges(hasAirlineChanges);
  }, [airline1, airline2, airline3, airline4, originalAirline1, originalAirline2, originalAirline3]);

  const logoutFunction = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("token");
    localStorage.removeItem("userDetails");
  };

  const handlePhotoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        showToast("Please select a valid image file", "error", 3000);
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be less than 5MB", "error", 3000);
        return;
      }

      // Read file and show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setPhotoPreview(imageData);
        setIsPhotoModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview) return;

    try {
      showLoader();
      // Convert base64 to blob and then to File
      const response = await fetch(photoPreview);
      const blob = await response.blob();
      const file = new File([blob], "profile-photo.png", { type: blob.type });

      // Call the updateProfilePicture API
      await updateProfilePicture(file);

      setProfilePhoto(photoPreview);
      setIsPhotoModalOpen(false);
      setPhotoPreview(null);
      showToast("Profile photo updated successfully", "success", 3000);
    } catch (error) {
      console.error("Failed to upload photo", error);
      showToast("Failed to upload photo", "error", 3000);
    } finally {
      hideLoader();
    }
  };

  const handlePhotoModalClose = () => {
    setIsPhotoModalOpen(false);
    setPhotoPreview(null);
  };

  // Load options dynamically using your getAirlines service
  const loadAirlineOptions = async (inputValue: string) => {
    try {
      const res = await getAirlines(inputValue);
      return (
        res.map((airline: AirlineData) => ({
          value: airline.id,
          label: airline.airlineName,
        })) || []
      );
    } catch (err) {
      console.error("Failed to load airlines", err);
      //showToast('Failed to load airlines', 'error', 3000);
      return [];
    }
  };

  // Check if an airline option matches the primary airline
  const isPrimaryAirline = (option: AirlineOption | null): boolean => {
    if (!option || !userProfile?.primaryAirline) return false;
    return option.value === userProfile.primaryAirline.id;
  };

  // When adding additional airlines for the first time
  const handleAddAdditionalAirline = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowAdditionalAirline(true);
    // If we're showing the form for the first time and there were no
    // additional airlines before, mark that we have changes
    if (userProfile?.additionalAirlines?.length === 0) {
      setHasChanges(true);
    }
  };

  // Handlers for airline selection changes
  const handleAirline1Change = (selectedOption: AirlineOption | null) => {
    // Don't allow selection if it matches primary airline
    if (isPrimaryAirline(selectedOption)) {
      return;
    }

    setAirline1(selectedOption);
    // If both additional airlines are the same, clear the second one
    if (airline2 && selectedOption && airline2.value === selectedOption.value) {
      setAirline2(null);
    }
    if (airline3 && selectedOption && airline3.value === selectedOption.value) {
      setAirline3(null);
    }
  };

  const handleAirline2Change = (selectedOption: AirlineOption | null) => {
    // Don't allow selection if it matches primary airline
    if (isPrimaryAirline(selectedOption)) {
      return;
    }

    setAirline2(selectedOption);
    // If both additional airlines are the same, clear the first one
    if (airline1 && selectedOption && airline1.value === selectedOption.value) {
      setAirline1(null);
    }
    if (airline3 && selectedOption && airline3.value === selectedOption.value) {
      setAirline3(null);
    }
  };

  const handleAirline3Change = (selectedOption: AirlineOption | null) => {
    if (isPrimaryAirline(selectedOption)) return;
    setAirline3(selectedOption);

    if (airline1 && selectedOption && airline1.value === selectedOption.value) {
      setAirline1(null);
    }
    if (airline2 && selectedOption && airline2.value === selectedOption.value) {
      setAirline2(null);
    }
  };

  // Save function to prepare data for submission
  const handleSave = () => {
    // Create the payload with the correct structure
    const additionalAirlineIds: number[] = [];

    if (airline1) {
      additionalAirlineIds.push(Number(airline1.value));
    }

    if (airline2) {
      additionalAirlineIds.push(Number(airline2.value));
    }
    if (airline3) {
      additionalAirlineIds.push(Number(airline3.value));
    }

    const payload = {
      fullName: userProfile?.fullName || "",
      primaryAirlineId: userProfile?.primaryAirline?.id || 0,
      homebaseId: userProfile?.homebase?.id || 0,
      additionalAirlineIds: additionalAirlineIds,
    };

    console.log("Data to save:", payload);

    // Call the update API
    updateUserProfile(payload)
      .then((response) => {
        // Handle success
        console.log("Profile updated successfully", response);

        // Show success toast
        showToast("Profile updated successfully.", "success", 3000);

        // Update the original state to match the current state
        setOriginalAirline1(airline1);
        setOriginalAirline2(airline2);
        setOriginalAirline3(airline3);
        setHasChanges(false);
      })
      .catch((error) => {
        // Handle error
        console.error("Failed to update profile", error);
        setError("Failed to update profile");
        showToast("Failed to update profile", "error", 3000);
      });
  };

  return (
    <div className="profile">
      <div className="bg-white p-6 rounded-xl">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          <i className="st-user text-grease-100 text-lg"></i>
          <p className="text-grease-100 text-base font-semibold">My Account</p>
        </div>

        {/* Profile Avatar Row */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <img className="w-14 h-14 rounded-full object-cover" src={profilePhoto} alt="Profile Avatar" />
              <div
                className="absolute inset-0 bg-black bg-opacity-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="st-edit text-white text-lg"></i>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoFileSelect}
              className="hidden"
            />
            <div>
              <p className="text-grease-100 text-base font-semibold">{username || userProfile?.fullName || "Username"}</p>
              <p className="text-gray-400 text-xs mt-0.5">Air host</p>
            </div>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Change photo"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        </div>

        {/* General Information */}
        <div>
          <label className="block mb-1.5 text-xs font-medium text-grease-100">Email Address</label>
          <input
            value={String(email || userProfile?.email || "")}
            disabled
            className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
          />
        </div>

        {/* Employment Details */}
        <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">Employment Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">Airline</label>
            <input
              disabled
              value={userProfile?.primaryAirline?.airlineName || ""}
              className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
            />
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">Homebase</label>
            <input
              disabled
              value={userProfile?.homebase?.name || ""}
              className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
            />
          </div>
        </div>

        {!showAdditionalAirline && userProfile?.additionalAirlines?.length === 0 && (
          <a href="#" onClick={handleAddAdditionalAirline} className="flex items-center gap-2 text-sm text-light-yellow font-medium my-6">
            <i className="st-add text-light-yellow text-sm"></i>
            Add Additional Airline
          </a>
        )}

        {/* Additional Airlines */}
        {(showAdditionalAirline || (userProfile?.additionalAirlines && userProfile.additionalAirlines.length > 0)) && (
          <>
            <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">Additional Airlines</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Primary Additional Airline</label>
                <AsyncSelect<AirlineOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Search and select airline"
                  value={airline1}
                  onChange={handleAirline1Change}
                  isOptionDisabled={(option) => isPrimaryAirline(option) || (airline2 !== null && option.value === airline2.value)}
                  styles={customAsyncStyles}
                />
                {isPrimaryAirline(airline1) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Tertiary Additional Airline</label>
                <AsyncSelect<AirlineOption>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Search and select airline"
                  value={airline2}
                  onChange={handleAirline2Change}
                  isOptionDisabled={(option) => isPrimaryAirline(option) || (airline1 !== null && option.value === airline1.value)}
                  styles={customAsyncStyles}
                />
                {isPrimaryAirline(airline2) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Tertiary Additional Airline</label>
                <AsyncSelect<AirlineOption>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Select airline"
                  value={airline3}
                  onChange={handleAirline3Change}
                  isOptionDisabled={(option) =>
                    isPrimaryAirline(option) ||
                    (airline1 !== null && option.value === airline1.value) ||
                    (airline2 !== null && option.value === airline2.value)
                  }
                  styles={customAsyncStyles}
                />
                {isPrimaryAirline(airline3) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            className="text-red-500 text-sm font-medium border border-red-200 rounded-lg px-5 py-2.5 hover:bg-red-50 transition-colors"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete Account
          </button>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <button
                onClick={() => {
                  setAirline1(originalAirline1);
                  setAirline2(originalAirline2);
                  setAirline3(originalAirline3);
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Discard
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`inline-flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-medium leading-none transition-colors ${
                hasChanges
                  ? "bg-[#FFBC00] text-white shadow-button hover:opacity-90"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit</span>
            </button>
          </div>
        </div>
      </div>
      {/* Photo Preview Modal */}
      {isPhotoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Preview Photo</h2>
              <button
                onClick={handlePhotoModalClose}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {photoPreview && (
              <div className="mb-6 flex justify-center">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-48 h-48 rounded-lg object-cover"
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={handlePhotoModalClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePhotoUpload}
                className="flex-1 px-4 py-2 text-white bg-[#FFBC00] rounded-lg hover:bg-[#E6A800] transition-colors"
              >
                Update Photo
              </button>
            </div>
          </div>
        </div>
      )}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)} // 👈 Close modal
      />
    </div>
  );
};

export default Profile;
